import { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import Icon from "@mdi/react";
import {
  mdiFilterOutline,
  mdiSortAscending,
  mdiSortDescending,
  mdiChevronUp,
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiClose,
  mdiArrowUp,
  mdiArrowDown,
  mdiCheck,
} from "@mdi/js";
import type { SortDescriptor } from "@heroui/react";
import { Button, NumberField, SearchField, Table, cn } from "@heroui/react";

import { ROUTES } from "@/constants/routes";
import { clientsStore } from "@/stores/ClientsStore";
import type { ClientsFilters } from "@/stores/ClientsStore";
import type { SuperadminClientListItemDto } from "@pro-sto/api-client";
import MetricsPanel from "./components/MetricsPanel";
import "./ClientsPage.css";

/* ───── Helpers ───── */

const formatMoney = (value: number | undefined | null) =>
  value == null
    ? "—"
    : value.toLocaleString("ru-RU", {
        style: "currency",
        currency: "RUB",
        maximumFractionDigits: 0,
      });

function getClientName(client: SuperadminClientListItemDto): string {
  return client.name ?? "Не указано";
}

function getClientPhone(client: SuperadminClientListItemDto): string {
  return client.phone ?? "Не указано";
}

function formatServiceCenters(client: SuperadminClientListItemDto): string {
  const centers =
    (client as unknown as { serviceCenters?: Array<{ name: string }> })
      .serviceCenters ?? client.serviceCenters;
  if (!centers || centers.length === 0) return "Не указано";
  return centers.map((sc) => sc.name).join(", ");
}

function formatServiceCentersShort(
  client: SuperadminClientListItemDto,
): string {
  const centers =
    (client as unknown as { serviceCenters?: Array<{ name: string }> })
      .serviceCenters ?? client.serviceCenters;
  if (!centers || centers.length === 0) return "Не указано";
  if (centers.length === 1) return centers[0].name;
  return `${centers.length} СЦ`;
}

/* ───── Sortable column header ───── */

function SortableColumnHeader({
  children,
  sortDirection,
}: {
  children: React.ReactNode;
  sortDirection?: "ascending" | "descending";
}) {
  return (
    <span className="cl-sortable-header">
      {children}
      {!!sortDirection && (
        <Icon
          path={mdiChevronUp}
          size={0.55}
          className={cn(
            "cl-sortable-header__icon",
            sortDirection === "descending" && "cl-sortable-header__icon--desc",
          )}
        />
      )}
    </span>
  );
}

/* ───── Highlight search text ───── */

function highlightText(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="cl-highlight">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

/* ───── Table columns ───── */

interface Column {
  id: string;
  label: string;
  width?: string;
  align?: "left" | "center" | "right";
  allowsSorting?: boolean;
  stickyLeft?: boolean;
  stickyRight?: boolean;
  render: (row: SuperadminClientListItemDto, query?: string) => React.ReactNode;
}

const SORTABLE_COLUMNS: { id: string; label: string }[] = [
  { id: "bookingsCount", label: "Бронирования" },
  { id: "totalSpent", label: "Потрачено" },
  { id: "created_at", label: "Дата регистрации" },
];

/* ───── Page component ───── */

const ClientsPage: React.FC = observer(() => {
  const navigate = useNavigate();

  const columns: Column[] = [
    {
      id: "name",
      label: "Имя",
      width: "220px",
      stickyLeft: true,
      render: (row, query) => (
        <span className="cl-name-cell" title={getClientName(row)}>
          {highlightText(getClientName(row), query ?? "")}
        </span>
      ),
    },
    {
      id: "phone",
      label: "Телефон",
      width: "180px",
      render: (row, query) => highlightText(getClientPhone(row), query ?? ""),
    },
    {
      id: "bookingsCount",
      label: "Броней за период",
      width: "140px",
      align: "center",
      allowsSorting: true,
      render: (row) =>
        row.bookingsCount == null ? "Не указано" : row.bookingsCount,
    },
    {
      id: "totalSpent",
      label: "Потрачено",
      width: "170px",
      align: "right",
      allowsSorting: true,
      render: (row) => formatMoney(row.totalSpent ?? undefined),
    },
    {
      id: "serviceCenters",
      label: "Сервисные центры",
      width: "260px",
      render: (row) => (
        <span
          className="cl-service-centers-cell"
          title={formatServiceCenters(row)}
        >
          {formatServiceCentersShort(row)}
        </span>
      ),
    },
    {
      id: "actions",
      label: "",
      width: "52px",
      align: "center",
      stickyRight: true,
      render: (row) => (
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label="Открыть"
          onPress={() => navigate(`${ROUTES.CLIENTS}/${row.uuid}`)}
        >
          <Icon path={mdiChevronRight} size={0.9} />
        </Button>
      ),
    },
  ];

  /* ───── Local panel state ───── */

  type ActivePopover = "filter" | "sort" | null;
  const [activePopover, setActivePopover] = useState<ActivePopover>(null);
  const isFilterOpen = activePopover === "filter";
  const isSortOpen = activePopover === "sort";

  const openFilter = () => {
    // Sync draft from store when opening the filter panel
    if (activePopover !== "filter") {
      setDraftServiceCenterIds(new Set(clientsStore.filters.serviceCenterIds));
      setDraftBookingsFrom(clientsStore.filters.bookings.from);
      setDraftBookingsTo(clientsStore.filters.bookings.to);
      setDraftSpentFrom(clientsStore.filters.spent.from);
      setDraftSpentTo(clientsStore.filters.spent.to);
    }
    setActivePopover((v) => (v === "filter" ? null : "filter"));
  };
  const openSort = () =>
    setActivePopover((v) => (v === "sort" ? null : "sort"));
  const closeAll = () => setActivePopover(null);

  const [isSCDropOpen, setIsSCDropOpen] = useState(false);

  // Infinite scroll sentinel
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelCallback = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0]?.isIntersecting &&
            clientsStore.hasNextPage &&
            !clientsStore.isLoadingMore
          ) {
            clientsStore.loadMore();
          }
        },
        { rootMargin: "200px" },
      );
      observerRef.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientsStore.hasNextPage, clientsStore.isLoadingMore],
  );

  // Local filter draft (applied on "Применить")
  const [draftServiceCenterIds, setDraftServiceCenterIds] = useState<
    Set<string>
  >(new Set(clientsStore.filters.serviceCenterIds));
  const [draftBookingsFrom, setDraftBookingsFrom] = useState<
    number | undefined
  >(clientsStore.filters.bookings.from);
  const [draftBookingsTo, setDraftBookingsTo] = useState<number | undefined>(
    clientsStore.filters.bookings.to,
  );
  const [draftSpentFrom, setDraftSpentFrom] = useState<number | undefined>(
    clientsStore.filters.spent.from,
  );
  const [draftSpentTo, setDraftSpentTo] = useState<number | undefined>(
    clientsStore.filters.spent.to,
  );

  const searchDebounceTimer = useRef<number>(0);

  const handleSearchChange = useCallback((v: string) => {
    clientsStore.setSearchValue(v);
    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = window.setTimeout(() => {
      clientsStore.loadAll();
    }, 400);
  }, []);

  const filterAnchorRef = useRef<HTMLDivElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const sortAnchorRef = useRef<HTMLDivElement>(null);
  const sortPanelRef = useRef<HTMLDivElement>(null);

  /* ───── Load data on mount ───── */

  useEffect(() => {
    clientsStore.loadAll();
  }, []);

  /* ───── Close on outside click ───── */

  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target as Node) &&
        filterAnchorRef.current &&
        !filterAnchorRef.current.contains(e.target as Node) &&
        sortPanelRef.current &&
        !sortPanelRef.current.contains(e.target as Node) &&
        sortAnchorRef.current &&
        !sortAnchorRef.current.contains(e.target as Node)
      ) {
        closeAll();
      } else if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target as Node) &&
        filterAnchorRef.current &&
        !filterAnchorRef.current.contains(e.target as Node)
      ) {
        if (isFilterOpen) closeAll();
      } else if (
        sortPanelRef.current &&
        !sortPanelRef.current.contains(e.target as Node) &&
        sortAnchorRef.current &&
        !sortAnchorRef.current.contains(e.target as Node)
      ) {
        if (isSortOpen) closeAll();
      }
    },
    [isFilterOpen, isSortOpen],
  );

  useEffect(() => {
    if (isFilterOpen || isSortOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isFilterOpen, isSortOpen, handleOutsideClick]);

  /* ───── Sort handler ───── */

  const handleSortChange = (descriptor: SortDescriptor) => {
    clientsStore.setSortDescriptor({
      column: String(descriptor.column),
      direction: descriptor.direction ?? "ascending",
    });
  };

  /* ───── Apply filters ───── */

  const applyFilters = () => {
    const next: ClientsFilters = {
      serviceCenterIds: Array.from(draftServiceCenterIds),
      bookings: { from: draftBookingsFrom, to: draftBookingsTo },
      spent: { from: draftSpentFrom, to: draftSpentTo },
    };
    clientsStore.setFilters(next);
    closeAll();
  };

  const resetLocalFilters = () => {
    setDraftServiceCenterIds(new Set());
    setDraftBookingsFrom(undefined);
    setDraftBookingsTo(undefined);
    setDraftSpentFrom(undefined);
    setDraftSpentTo(undefined);
  };

  const resetAndApplyFilters = () => {
    resetLocalFilters();
    clientsStore.resetFilters();
    closeAll();
  };

  /* ───── Derived ───── */

  const isBlocked = clientsStore.isLoading;

  const searchLower = clientsStore.search.toLowerCase();
  const { activeMonth } = clientsStore;
  const sortDesc: SortDescriptor = {
    column: clientsStore.sortDescriptor.column,
    direction: clientsStore.sortDescriptor.direction,
  };

  return (
    <div className="cl-page">
      {/* ───── Toolbar ───── */}
      <div className="cl-page__toolbar">
        <div className="cl-page__toolbar-left">
          <div className="cl-page__search">
            <SearchField
              value={clientsStore.search}
              onChange={handleSearchChange}
              aria-label="Поиск клиентов"
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder="Имя или телефон..." />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          </div>

          {/* Filter button */}
          <div className="cl-filter-anchor" ref={filterAnchorRef}>
            <Button
              isIconOnly
              variant="tertiary"
              aria-label="Фильтры"
              isDisabled={isBlocked}
              onPress={openFilter}
              className={
                isFilterOpen
                  ? "filter-btn--active icon-btn--card"
                  : "icon-btn--card"
              }
            >
              <Icon path={mdiFilterOutline} size={0.85} />
            </Button>

            {/* ───── Filter panel ───── */}
            {isFilterOpen && (
              <div className="cl-filter-panel" ref={filterPanelRef}>
                <div className="cl-filter-panel__header">
                  <span className="cl-filter-panel__title">Фильтры</span>
                  <Button
                    isIconOnly
                    variant="ghost"
                    size="sm"
                    aria-label="Закрыть фильтры"
                    onPress={() => closeAll()}
                  >
                    <Icon path={mdiClose} size={0.7} />
                  </Button>
                </div>

                <div className="cl-filter-panel__body">
                  {/* Сервисный центр */}
                  <div className="cl-filter-panel__group">
                    <span className="cl-filter-panel__label">
                      Сервисный центр
                    </span>
                    <div className="cl-sc-dropdown">
                      <button
                        type="button"
                        className="cl-sc-dropdown__trigger"
                        onClick={() => setIsSCDropOpen((v) => !v)}
                      >
                        <span className="cl-sc-dropdown__trigger-text">
                          {draftServiceCenterIds.size === 0
                            ? "Все центры"
                            : Array.from(draftServiceCenterIds)
                                .map(
                                  (id) =>
                                    clientsStore.serviceCenterOptions.find(
                                      (sc) => sc.uuid === id,
                                    )?.name ?? id,
                                )
                                .join(" и ")}
                        </span>
                        <Icon
                          path={isSCDropOpen ? mdiChevronUp : mdiChevronDown}
                          size={0.75}
                          className="cl-sc-dropdown__chevron"
                        />
                      </button>
                      {isSCDropOpen && (
                        <div className="cl-sc-dropdown__list">
                          {clientsStore.serviceCenterOptions.map((sc) => {
                            const checked = draftServiceCenterIds.has(sc.uuid);
                            return (
                              <button
                                key={sc.uuid}
                                type="button"
                                className="cl-sc-dropdown__item"
                                onClick={() => {
                                  const next = new Set(draftServiceCenterIds);
                                  if (checked) next.delete(sc.uuid);
                                  else next.add(sc.uuid);
                                  setDraftServiceCenterIds(next);
                                }}
                              >
                                <span className="cl-sc-dropdown__item-pill">
                                  <span className="cl-sc-dropdown__item-name">
                                    {sc.name}
                                  </span>
                                  {checked && (
                                    <Icon
                                      path={mdiCheck}
                                      size={0.55}
                                      className="cl-sc-dropdown__check"
                                    />
                                  )}
                                </span>
                              </button>
                            );
                          })}
                          {clientsStore.isServiceCentersLoading && (
                            <div className="cl-sc-dropdown__loading">
                              Загрузка...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Бронирования */}
                  <div className="cl-filter-panel__group">
                    <span className="cl-filter-panel__label">
                      Кол-во бронирований
                    </span>
                    <div className="cl-filter-panel__range">
                      <NumberField
                        value={draftBookingsFrom}
                        onChange={(v) =>
                          setDraftBookingsFrom(isNaN(v) ? undefined : v)
                        }
                        minValue={0}
                        formatOptions={{ maximumFractionDigits: 0 }}
                        className="cl-filter-panel__number-field"
                      >
                        <NumberField.Group className="w-full">
                          <NumberField.Input
                            placeholder="От"
                            className="w-full"
                          />
                        </NumberField.Group>
                      </NumberField>
                      <span className="cl-filter-panel__separator">—</span>
                      <NumberField
                        value={draftBookingsTo}
                        onChange={(v) =>
                          setDraftBookingsTo(isNaN(v) ? undefined : v)
                        }
                        minValue={0}
                        formatOptions={{ maximumFractionDigits: 0 }}
                        className="cl-filter-panel__number-field"
                      >
                        <NumberField.Group className="w-full">
                          <NumberField.Input
                            placeholder="До"
                            className="w-full"
                          />
                        </NumberField.Group>
                      </NumberField>
                    </div>
                  </div>

                  {/* Потраченная сумма */}
                  <div className="cl-filter-panel__group">
                    <span className="cl-filter-panel__label">Потрачено, ₽</span>
                    <div className="cl-filter-panel__range">
                      <NumberField
                        value={draftSpentFrom}
                        onChange={(v) =>
                          setDraftSpentFrom(isNaN(v) ? undefined : v)
                        }
                        minValue={0}
                        formatOptions={{ maximumFractionDigits: 0 }}
                        className="cl-filter-panel__number-field"
                      >
                        <NumberField.Group className="w-full">
                          <NumberField.Input
                            placeholder="От"
                            className="w-full"
                          />
                        </NumberField.Group>
                      </NumberField>
                      <span className="cl-filter-panel__separator">—</span>
                      <NumberField
                        value={draftSpentTo}
                        onChange={(v) =>
                          setDraftSpentTo(isNaN(v) ? undefined : v)
                        }
                        minValue={0}
                        formatOptions={{ maximumFractionDigits: 0 }}
                        className="cl-filter-panel__number-field"
                      >
                        <NumberField.Group className="w-full">
                          <NumberField.Input
                            placeholder="До"
                            className="w-full"
                          />
                        </NumberField.Group>
                      </NumberField>
                    </div>
                  </div>
                </div>

                <div className="cl-filter-panel__footer">
                  <Button
                    variant="tertiary"
                    size="sm"
                    onPress={resetAndApplyFilters}
                  >
                    Сбросить
                  </Button>
                  <Button variant="primary" size="sm" onPress={applyFilters}>
                    Применить
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Sort button */}
          <div className="cl-sort-anchor" ref={sortAnchorRef}>
            <Button
              isIconOnly
              variant="tertiary"
              aria-label="Сортировка"
              isDisabled={isBlocked}
              onPress={openSort}
              className={
                isSortOpen
                  ? "filter-btn--active icon-btn--card"
                  : "icon-btn--card"
              }
            >
              <Icon
                path={
                  clientsStore.sortDescriptor.direction === "descending"
                    ? mdiSortDescending
                    : mdiSortAscending
                }
                size={0.85}
              />
            </Button>

            {/* ───── Sort panel ───── */}
            {isSortOpen && (
              <div className="cl-sort-panel" ref={sortPanelRef}>
                <div className="cl-sort-panel__header">
                  <span className="cl-sort-panel__title">Сортировка</span>
                  <Button
                    isIconOnly
                    variant="ghost"
                    size="sm"
                    aria-label="Закрыть сортировку"
                    onPress={() => closeAll()}
                  >
                    <Icon path={mdiClose} size={0.7} />
                  </Button>
                </div>

                <div className="cl-sort-panel__body">
                  {SORTABLE_COLUMNS.map((col) => {
                    const isActive =
                      clientsStore.sortDescriptor.column === col.id;
                    return (
                      <div
                        key={col.id}
                        className={cn(
                          "cl-sort-panel__item",
                          isActive && "cl-sort-panel__item--active",
                        )}
                      >
                        <span className="cl-sort-panel__item-label">
                          {col.label}
                        </span>
                        <div className="cl-sort-panel__item-actions">
                          <Button
                            isIconOnly
                            size="sm"
                            variant={
                              isActive &&
                              clientsStore.sortDescriptor.direction ===
                                "ascending"
                                ? "primary"
                                : "tertiary"
                            }
                            aria-label={`${col.label} по возрастанию`}
                            onPress={() =>
                              clientsStore.setSortDescriptor({
                                column: col.id,
                                direction: "ascending",
                              })
                            }
                          >
                            <Icon path={mdiArrowUp} size={0.65} />
                          </Button>
                          <Button
                            isIconOnly
                            size="sm"
                            variant={
                              isActive &&
                              clientsStore.sortDescriptor.direction ===
                                "descending"
                                ? "primary"
                                : "tertiary"
                            }
                            aria-label={`${col.label} по убыванию`}
                            onPress={() =>
                              clientsStore.setSortDescriptor({
                                column: col.id,
                                direction: "descending",
                              })
                            }
                          >
                            <Icon path={mdiArrowDown} size={0.65} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Month navigator */}
          <div className="cl-month-nav">
            <Button
              isIconOnly
              variant="tertiary"
              size="sm"
              aria-label="Предыдущий месяц"
              className="icon-btn--card"
              isDisabled={isBlocked}
              onPress={() => clientsStore.goToPrevMonth()}
            >
              <Icon path={mdiChevronLeft} size={0.85} />
            </Button>
            <span className="cl-month-nav__label">
              {activeMonth
                .toLocaleString("ru-RU", { month: "long", year: "numeric" })
                .replace(/\s*г\.?\s*$/i, "")}
            </span>
            <Button
              isIconOnly
              variant="tertiary"
              size="sm"
              aria-label="Следующий месяц"
              className="icon-btn--card"
              isDisabled={isBlocked}
              onPress={() => clientsStore.goToNextMonth()}
            >
              <Icon path={mdiChevronRight} size={0.85} />
            </Button>
          </div>
        </div>
      </div>

      {/* ───── Metrics ───── */}
      <div className="cl-metrics-area">
        <MetricsPanel />
      </div>

      {/* ───── Table ───── */}
      <div
        className={`cl-page__table-wrap${isBlocked ? " cl-page__table-wrap--blocked" : ""}`}
      >
        <Table>
          <Table.ScrollContainer>
            <Table.Content
              aria-label="Клиенты"
              className="cl-table"
              style={{ minWidth: "860px" }}
              sortDescriptor={sortDesc}
              onSortChange={handleSortChange}
            >
              <Table.Header className="cl-table__head sticky top-0 z-10">
                {columns.map((col) => (
                  <Table.Column
                    key={col.id}
                    id={col.id}
                    isRowHeader={col.id === "name"}
                    allowsSorting={col.allowsSorting}
                    className={classNames("cl-table__th", {
                      "cl-table__th_center": col.align === "center",
                      "cl-table__th_right": col.align === "right",
                      "cl-table__th_sticky-left": col.stickyLeft,
                      "cl-table__th_sticky-right": col.stickyRight,
                    })}
                    style={{
                      ...(col.width ? { width: col.width } : {}),
                      ...(col.stickyLeft ? { left: 0 } : {}),
                    }}
                  >
                    {col.allowsSorting
                      ? ({ sortDirection }) => (
                          <SortableColumnHeader sortDirection={sortDirection}>
                            {col.label}
                          </SortableColumnHeader>
                        )
                      : col.label}
                  </Table.Column>
                ))}
              </Table.Header>
              <Table.Body className="cl-table__body">
                {clientsStore.visibleClients.length === 0 &&
                !clientsStore.isLoading ? (
                  <Table.Row className="cl-table__row">
                    <Table.Cell
                      className="cl-table__empty-cell"
                      colSpan={columns.length}
                    >
                      <div className="cl-table__empty-state">Нет клиентов</div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  <>
                    {clientsStore.visibleClients.map((row) => (
                      <Table.Row
                        key={row.uuid}
                        id={row.uuid}
                        className="cl-table__row"
                      >
                        {columns.map((col) => (
                          <Table.Cell
                            key={col.id}
                            className={classNames("cl-table__td", {
                              "cl-table__td_center": col.align === "center",
                              "cl-table__td_right": col.align === "right",
                              "cl-table__td_sticky-left": col.stickyLeft,
                              "cl-table__td_sticky-right": col.stickyRight,
                            })}
                            style={col.stickyLeft ? { left: 0 } : undefined}
                          >
                            {col.render(row, searchLower)}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                    {clientsStore.hasNextPage && (
                      <Table.Row
                        ref={sentinelCallback}
                        className="cl-table__row cl-table__row--sentinel"
                      >
                        <Table.Cell
                          className="cl-table__td"
                          colSpan={columns.length}
                        >
                          {clientsStore.isLoadingMore && (
                            <div className="cl-table__loading-more">
                              Загрузка...
                            </div>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </>
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>
    </div>
  );
});

ClientsPage.displayName = "ClientsPage";

export default ClientsPage;
